import {
  DirectionEnum,
  IDropdownItem,
  INVALID_RIDE_DRAG_STATUSES,
  IPassengerInfo,
  IRideDto,
  IRideTableDto,
  timeDiffMinutesCeiling,
} from "@ryde/shared";

import {
  RIDE_BULK_MOVE_AREAS_DELIMITER,
  RIDE_BULK_MOVE_LABEL_DELIMITER,
  RIDE_BULK_MOVE_TARGET_STATUSES,
} from "./ride-stations-panel.constants";

/**
 * The time a move compares rides by: a pickup ride is anchored on its arrival, a drop-off ride
 * on its departure — the same anchors the drag time range uses. A ride that is neither (Both /
 * None) has no anchor, and the drag range does not apply to those either.
 */
export const getRideBulkMoveAnchorTime = (
  ride: Partial<IRideDto>,
): TDateISO | undefined => {
  switch (ride.direction) {
    case DirectionEnum.PickUp:
      return ride.rideEnd;
    case DirectionEnum.DropOff:
      return ride.rideStart;
    default:
      return undefined;
  }
};

/** Mirrors the drag flow's source guard — passengers cannot leave a closed ride. */
export const isRideBulkMoveAllowedFromRide = (
  ride: Partial<IRideDto>,
): boolean => {
  return !ride.status || !INVALID_RIDE_DRAG_STATUSES.includes(ride.status);
};

export const getRideBulkMoveSelectedPassengers = ({
  ride,
  selectedPassengersIds,
}: {
  ride: Partial<IRideDto>;
  selectedPassengersIds: number[];
}): IPassengerInfo[] => {
  if (!selectedPassengersIds?.length) {
    return [];
  }

  const selectedPassengersIdsSet = new Set(selectedPassengersIds);

  return (ride.metadata?.passengersInfo ?? []).filter(passenger =>
    selectedPassengersIdsSet.has(passenger.id),
  );
};

export const getRideBulkMoveTargetOptions = ({
  rides,
  originRide,
  areaLabelsById,
  formatAnchorTime,
}: {
  rides: IRideTableDto[];
  originRide: Partial<IRideDto>;
  areaLabelsById: Map<number, string>;
  formatAnchorTime: (dateISO: TDateISO | undefined) => string;
}): IDropdownItem[] => {
  return rides
    .filter(
      ride =>
        ride.id !== originRide.id &&
        ride.direction === originRide.direction &&
        RIDE_BULK_MOVE_TARGET_STATUSES.includes(ride.status),
    )
    .map(ride => {
      const areasLabel = [ride.originAreaId, ride.destinationAreaId]
        .map(areaId => areaLabelsById.get(Number(areaId)))
        .filter(Boolean)
        .join(RIDE_BULK_MOVE_AREAS_DELIMITER);
      const timeLabel = formatAnchorTime(
        getRideBulkMoveAnchorTime(ride) ?? ride.rideStart,
      );
      const item: IDropdownItem = {
        value: ride.id,
        label: `${[String(ride.id), areasLabel]
          .filter(Boolean)
          .join(RIDE_BULK_MOVE_LABEL_DELIMITER)} (${timeLabel})`,
      };

      return item;
    });
};

/**
 * A move is not limited by the drag time range — a wider gap only warrants the extra
 * approval prompt (RYDE-8146).
 */
export const isRideBulkMoveTimeGapExceeded = ({
  originRide,
  targetRide,
  maxGapMinutes,
}: {
  originRide: Partial<IRideDto>;
  targetRide: Partial<IRideDto>;
  maxGapMinutes: number;
}): boolean => {
  const originAnchorTime = getRideBulkMoveAnchorTime(originRide);
  const targetAnchorTime = getRideBulkMoveAnchorTime(targetRide);

  if (!originAnchorTime || !targetAnchorTime) {
    return false;
  }

  return (
    timeDiffMinutesCeiling(originAnchorTime, targetAnchorTime) > maxGapMinutes
  );
};
